// src/components/EasterEggs/BugShooterGame.jsx
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom'; // Add this import
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { Bug as BugIcon, X, Zap, Trophy } from 'lucide-react';
import { useScroll } from '../../contexts/ScrollContext';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

const bugMovement = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(5px, -5px) rotate(5deg); }
  50% { transform: translate(0, -8px) rotate(-5deg); }
  75% { transform: translate(-5px, -3px) rotate(10deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

const bugDeath = keyframes`
  0% { transform: scale(1) rotate(0); opacity: 1; }
  20% { transform: scale(1.2) rotate(10deg); opacity: 1; }
  100% { transform: scale(0) rotate(180deg); opacity: 0; }
`;

// Global style that ensures the game UI is outside of the custom scrollbar context
const GlobalGameStyle = createGlobalStyle`
  body.game-active {
    cursor: crosshair !important;
  }
`;

// Game window that contains all bugs
const GameWindow = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: all;
`;

const GameControls = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: ${({ theme }) => theme.colors.card};
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  animation: ${fadeIn} 0.3s ease-out;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  pointer-events: all;
`;

const ScoreDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const GameStartModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${({ theme }) => theme.colors.card};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  animation: ${fadeIn} 0.3s ease-out;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  pointer-events: all;
  
  h2 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.text};
  }
  
  p {
    margin-bottom: 1.5rem;
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.6;
  }
`;

const StartButton = styled.button`
  padding: 0.8rem 2rem;
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  border: none;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0 auto;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px ${({ theme }) => `${theme.colors.accent}50`};
  }
`;

const BugElement = styled.div`
  position: fixed;
  top: ${props => props.$top}vh;
  left: ${props => props.$left}vw;
  transform-origin: center;
  animation: ${bugMovement} 2s infinite ease;
  animation-delay: ${props => props.$delay}s;
  color: ${props => props.$color || '#ff6b6b'};
  pointer-events: all;
  cursor: crosshair;
  will-change: transform;
  
  &.dying {
    animation: ${bugDeath} 0.5s forwards ease-out;
    pointer-events: none;
  }
  
  svg {
    filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.3));
  }
`;

const Timer = styled.div`
  height: 8px;
  background: ${({ theme }) => `${theme.colors.secondary}50`};
  border-radius: 4px;
  width: 120px;
  overflow: hidden;
  
  .bar {
    height: 100%;
    background: ${({ theme }) => theme.colors.accent};
    width: ${props => props.$progress}%;
    transition: width 0.2s linear;
  }
`;

const GameResultModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${({ theme }) => theme.colors.card};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  animation: ${fadeIn} 0.3s ease-out;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  pointer-events: all;
  
  h2 {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.text};
  }
  
  .score {
    font-size: 4rem;
    color: ${({ theme }) => theme.colors.accent};
    margin: 1rem 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }
  
  .message {
    margin-bottom: 2rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  
  .buttons {
    display: flex;
    justify-content: center;
    gap: 1rem;
  }
  
  button {
    padding: 0.7rem 1.5rem;
    border-radius: 30px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  button.play-again {
    background: ${({ theme }) => theme.colors.accent};
    color: white;
    border: none;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 5px 15px ${({ theme }) => `${theme.colors.accent}50`};
    }
  }
  
  button.close {
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.text};
    
    &:hover {
      background: ${({ theme }) => theme.colors.secondary};
    }
  }
`;

// Cancel button styled consistently
const CancelButton = styled.button`
  padding: 0.8rem 2rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }
`;

// Keep track of whether the bug shooter game has been played in this session
// We'll use sessionStorage for this purpose
const hasPlayedGame = () => {
  return sessionStorage.getItem('bugShooterGamePlayed') === 'true';
};

const setGamePlayed = () => {
  sessionStorage.setItem('bugShooterGamePlayed', 'true');
};

// Bug contact hover detector for the Easter Egg with a more difficult trigger
const BugContactDetector = () => {
  const [hoverCount, setHoverCount] = useState(0);
  const [hoverPattern, setHoverPattern] = useState([]);
  const [showGame, setShowGame] = useState(false);
  const lastHoverTime = useRef(Date.now());
  const hoverTimeoutRef = useRef(null);
  
  // Much more challenging pattern to trigger the game
  // Now requires 10 hover actions in a specific pattern (in-out-in-out...)
  const handleHover = (isEntering) => {
    // Don't trigger if game has already been played this session
    if (hasPlayedGame()) return;
    
    const now = Date.now();
    
    // Only count hovers that are within 2 seconds of each other (shorter timeframe)
    if (now - lastHoverTime.current < 2000) {
      // Add the current hover action to the pattern (true for entering, false for leaving)
      setHoverPattern(prev => {
        // Don't add duplicate consecutive hover states (must be alternating)
        if (prev.length > 0 && prev[prev.length - 1] === isEntering) {
          return prev;
        }
        return [...prev, isEntering];
      });
      
      // Increment hover count only on mouseenter events
      if (isEntering) {
        setHoverCount(prev => prev + 1);
      }
      
      // Clear existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      
      // Check for the correct pattern: need 10 total hovers with alternating in/out pattern
      // This is much harder to trigger accidentally
      if (hoverCount >= 9 && hoverPattern.length >= 19 && isAlternatingPattern(hoverPattern)) {
        setShowGame(true);
        setHoverCount(0);
        setHoverPattern([]);
        return;
      }
    } else {
      // Reset if too much time has passed
      setHoverCount(isEntering ? 1 : 0);
      setHoverPattern(isEntering ? [isEntering] : []);
    }
    
    lastHoverTime.current = now;
    
    // Reset hover count after just 2 seconds of no hovering (makes it harder)
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverCount(0);
      setHoverPattern([]);
    }, 2000);
  };
  
  // Helper function to check if the pattern is correctly alternating between true and false
  const isAlternatingPattern = (pattern) => {
    if (pattern.length < 19) return false; // Need at least 19 events (10 in, 9 out or vice versa)
    
    // Check that the pattern alternates between true and false
    for (let i = 1; i < pattern.length; i++) {
      if (pattern[i] === pattern[i-1]) return false;
    }
    return true;
  };
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  return (
    <>
      <div 
        className="bug-detector" 
        onMouseEnter={() => handleHover(true)}
        onMouseLeave={() => handleHover(false)}
        style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', top: 0, left: 0 }}
      />
      {showGame && <BugShooterGame onClose={() => setShowGame(false)} />}
    </>
  );
};

// Main Bug Shooter Game - using React Portal for z-index isolation
const BugShooterGame = ({ onClose }) => {
  const [gameState, setGameState] = useState('intro'); // 'intro', 'playing', 'gameOver'
  const [score, setScore] = useState(0);
  const [bugs, setBugs] = useState([]);
  const [timeLeft, setTimeLeft] = useState(30); // 30 seconds game time
  const timerRef = useRef(null);
  const gameStarted = useRef(false);
  
  // Access the scroll instance from context
  const { scrollInstance } = useScroll();
  
  // Create a portal container for the game UI
  useEffect(() => {
    // Create a div to hold our game elements
    const portalContainer = document.createElement('div');
    portalContainer.id = 'bug-game-portal';
    portalContainer.style.position = 'fixed';
    portalContainer.style.inset = '0';
    portalContainer.style.zIndex = '9999999';
    portalContainer.style.pointerEvents = 'none';
    
    // Add it to the body
    document.body.appendChild(portalContainer);
    
    return () => {
      // Clean up on unmount
      if (document.body.contains(portalContainer)) {
        document.body.removeChild(portalContainer);
      }
    };
  }, []);
  
  // Function to create a bug at a specific position
  const createBug = (id) => {
    return {
      id: id || Date.now() + Math.random(),
      // Keep bugs within the center 70% of the screen
      top: Math.random() * 70 + 15,
      left: Math.random() * 70 + 15,
      size: Math.floor(Math.random() * 20) + 20, // 20-40px
      color: getRandomBugColor(),
      delay: Math.random() * 2,
      dying: false
    };
  };
  
  // Start the game
  const startGame = () => {
    // Prevent duplicate starts
    if (gameStarted.current) return;
    gameStarted.current = true;
    
    // Mark that the game has been played this session
    setGamePlayed();
    
    // Update game state
    setGameState('playing');
    setScore(0);
    setTimeLeft(30);
    setBugs([]);
    
    // Change cursor globally
    document.body.classList.add('game-active');
    
    // Disable scrolling in the custom scrollbar if we have access to it
    if (scrollInstance) {
      scrollInstance.options('overflow', { x: 'hidden', y: 'hidden' });
    }
    
    // Create initial bugs (30 to fill the screen) - INCREASED FROM 15 TO 30
    const initialBugs = [];
    for (let i = 0; i < 30; i++) {
      initialBugs.push(createBug(i));
    }
    setBugs(initialBugs);
    
    // Start timer
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  // End the game
  const endGame = () => {
    setGameState('gameOver');
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };
  
  // Add new bugs
  const addBugs = (count) => {
    // Only add bugs if the game is still playing
    if (gameState !== 'playing') return;
    
    const newBugs = [];
    for (let i = 0; i < count; i++) {
      newBugs.push(createBug());
    }
    setBugs(prev => [...prev, ...newBugs]);
  };
  
  // Get random bug color
  const getRandomBugColor = () => {
    const colors = ['#ff6b6b', '#ff922b', '#5c940d', '#339af0', '#9c36b5'];
    return colors[Math.floor(Math.random() * colors.length)];
  };
  
  // HandleBugClick function - spawn 2 bugs instead of 1 and faster respawn time
  const handleBugClick = (bugId) => {
    // Play squash sound
    playSquashSound();
    
    // Mark the bug as dying
    setBugs(prev => 
      prev.map(bug => 
        bug.id === bugId ? { ...bug, dying: true } : bug
      )
    );
    
    // Update score
    setScore(prev => prev + 1);
    
    // Remove bug after animation and add MORE bugs FASTER
    setTimeout(() => {
      if (gameState === 'playing') {
        setBugs(prev => prev.filter(bug => bug.id !== bugId));
        // Spawn 2 bugs instead of 1 and shorten the time to 300ms instead of 500ms
        addBugs(2);
      }
    }, 300);
  };
  
  // Sound effect for squashing bugs
  const playSquashSound = () => {
    try {
      const sound = new Audio('/sounds/squash.mp3');
      sound.volume = 0.3;
      sound.play().catch(e => console.log("Audio play error:", e));
    } catch (error) {
      console.log("Could not play sound:", error);
    }
  };
  
  // Handle final close
  const handleClose = () => {
    // Clean up the game
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Reset flags
    gameStarted.current = false;
    
    // Remove cursor styling
    document.body.classList.remove('game-active');
    
    // Re-enable scrolling in the custom scrollbar
    if (scrollInstance) {
      scrollInstance.options('overflow', { x: 'hidden', y: 'scroll' });
    }
    
    // Call parent onClose
    onClose();
  };
  
  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Remove cursor styling
      document.body.classList.remove('game-active');
      
      // Re-enable scrolling in the custom scrollbar
      if (scrollInstance) {
        scrollInstance.options('overflow', { x: 'hidden', y: 'scroll' });
      }
    };
  }, [scrollInstance]);
  
  // Get message based on score
  const getScoreMessage = () => {
    if (score < 10) return "This is just sad.";
    if (score < 20) return "Still very embarrassing.";
    if (score < 30) return "I dont know what to say.";
    if (score < 40) return "You're getting there.";
    if (score < 50) return "Are you bored?";
    if (score < 60) return "Its getting old now.";
    if (score < 70) return "Kinda getting sad again.";
    return "You have no life.";
  };
  
  // Check if the portal container exists before rendering
  const portalContainer = document.getElementById('bug-game-portal');
  
  return (
    <>
      <GlobalGameStyle />
      {portalContainer && ReactDOM.createPortal(
        <>
          {gameState === 'playing' && (
            <GameWindow>
              {bugs.map(bug => (
                <BugElement
                  key={bug.id}
                  $top={bug.top}
                  $left={bug.left}
                  $color={bug.color}
                  $delay={bug.delay}
                  className={bug.dying ? 'dying' : ''}
                  onClick={() => handleBugClick(bug.id)}
                >
                  <BugIcon size={bug.size} fill={bug.color} />
                </BugElement>
              ))}
            </GameWindow>
          )}
          
          {gameState === 'playing' && (
            <GameControls>
              <ScoreDisplay>
                <Zap color="#ffd43b" size={18} />
                <span>{score}</span>
              </ScoreDisplay>
              
              <Timer $progress={(timeLeft / 30) * 100}>
                <div className="bar"></div>
              </Timer>
              
              <CloseButton onClick={handleClose} aria-label="Close game">
                <X size={18} />
              </CloseButton>
            </GameControls>
          )}
          
          {gameState === 'intro' && (
            <GameStartModal>
              <h2>Bug Hunter</h2>
              <p>
                You've found a hidden feature! Quick, those pesky bugs are trying to infest your website!
                Click on them as fast as you can to squash them before time runs out.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <StartButton onClick={startGame}>
                  <BugIcon size={18} /> Start Game
                </StartButton>
                <CancelButton onClick={handleClose}>
                  <X size={18} /> Cancel
                </CancelButton>
              </div>
            </GameStartModal>
          )}
          
          {gameState === 'gameOver' && (
            <GameResultModal>
              <h2>Game Over!</h2>
              <div className="score">
                <Trophy size={48} />
                {score}
              </div>
              <div className="message">{getScoreMessage()}</div>
              <div className="buttons">
                <button 
                  className="play-again" 
                  onClick={() => {
                    // Reset game state before starting again
                    gameStarted.current = false;
                    setGameState('playing');
                    startGame();
                  }}
                >
                  Play Again
                </button>
                <button className="close" onClick={handleClose}>
                  Close
                </button>
              </div>
            </GameResultModal>
          )}
        </>,
        portalContainer
      )}
    </>
  );
};

// Export the BugContactDetector component as a named export
export { BugContactDetector };

// Export the BugShooterGame as default export
export default BugShooterGame;